import {$qs} from '../../@shared/ts/dom';
import {getValue, elementHasChanged} from './ui-utils';

/**
 * Array containing form elements that have changed since the page load.
 */
const changedElements: HTMLElement[] = [];

/**
 * This is set to true on form submission.
 */
let formSubmitted = false;

/**
 * Returns text to render inside the banner.
 */
function getBannerText(key: string) {
	switch (key) {
		case 'saved-changes':
			return 'Changes were saved!';
		case 'unsaved-changes':
			return 'Careful — you have unsaved changes!';
		case 'saving-changes':
			return 'Your changes are being saved...';
		default:
			return 'Uh-oh... something went wrong!';
	}
}

/**
 * Returns the appropriate button for a PeachPay banner given 'key'. These buttons
 * are structured similarly to the WordPress banners to reduce complication with
 * styling/event-handling the PeachPay banners created with a WP function.
 */
function getButton(key: string, isDismiss: Boolean) {
	const $button = document.createElement('button');
	$button.type = 'button';
	$button.className = `${key}-button`;

	if (isDismiss) {
		$button.classList.add('pp-notice-dismiss');
		$button.addEventListener('click', () => {
			removeBanner(`#${key}-peachpay_message`);
		});
		return $button;
	}

	if (key === 'unsaved-changes') {
		$button.innerHTML = 'Save changes';
		$button.addEventListener('click', () => {
			const $ppform = $qs<HTMLFormElement>('form[action="options.php"]');
			if ($ppform) {
				$ppform.requestSubmit();
			} else {
				($qs('button[name=save]'))!.click();
			}
		});
	}

	return $button;
}

/**
 * Adds a PeachPay banner, using 'type' to render the correct style and
 * 'key' to render the correct text and button.
 */
export function addBanner(type: string, key: string, hasAction: Boolean, isDismissible: Boolean) {
	const $banner = document.createElement('div');
	$banner.id = `${key}-peachpay_message`;
	$banner.classList.add('notice', `notice-${type}`, 'addition');

	const $bannerChild1 = document.createElement('p');
	const $textElement = document.createElement('strong');
	$textElement.innerHTML = getBannerText(key);
	$bannerChild1.appendChild($textElement);

	$banner.appendChild($bannerChild1);

	if (hasAction || isDismissible) {
		const $bannerChild2 = document.createElement('div');

		if (hasAction) {
			const $button1 = getButton(key, false);
			$bannerChild2.appendChild($button1);
		}

		if (isDismissible) {
			const $button2 = getButton(key, true);
			$bannerChild2.appendChild($button2);
		}

		if ($bannerChild2.children.length > 1) {
			$bannerChild2.classList.add('pp-banner-buttons');
		}

		$banner.appendChild($bannerChild2);
	}

	document.querySelector('.peachpay-notices-container')?.prepend($banner);
}

/**
 * Removes a PeachPay banner with the given key; animates the fade-out.
 */
export function removeBanner(selector: string) {
	const $banner = document.querySelector(`${selector}`);
	$banner?.classList.add('removal');
	setTimeout(() => {
		$banner?.remove();
	}, 150);
}

/**
 * Called by listeners in initUnsavedChangesBanner and initOnLeaveWarning to render a
 * visual indication that the field data has changed.
 */
function markChangedField($element: HTMLElement, elementHasChanged: Boolean) {
	let $elementToMark = $element;
	// for some inputs, it is more visually-appealing to highlight a parent or neighboring element.
	if ($element.id === 'pp-merchant-logo-img-field') {
		$elementToMark = ($element.nextElementSibling as HTMLElement)?.querySelector('.pp-merchant-logo-border') ?? $element;
	} else if ($element.getAttribute('type') === 'checkbox') {
		$elementToMark = $element.nextElementSibling as HTMLElement;
	} else if ($element.hasAttribute('data-select2-id')) {
		$elementToMark = $element.nextElementSibling as HTMLElement;
	} else if ($element.getAttribute('type') === 'color') {
		$elementToMark = $element.closest('.pp-merged-inputs') ?? $element;
	}

	if (elementHasChanged) {
		$elementToMark?.classList.add('indicate-unsaved-change');
	} else {
		$elementToMark?.classList.remove('indicate-unsaved-change');
	}
}

/**
 * Called by initUnsavedChangesBanner when potential change to the form data is detected.
 * Adds the banner if there are actually unsaved changes (and the banner hasn't already
 * been added to the page) and removes the banner if there aren't.
 */
function handleUnsavedChangesBanner($target: HTMLElement, elementHasChanged: Boolean) {
	$target = ($target as HTMLInputElement).type === 'radio' ? $target.closest('section')! : $target;
	if (elementHasChanged) {
		if (changedElements.length === 0) {
			removeBanner('#unsaved-changes-peachpay_message');
			addBanner('warning', 'unsaved-changes', true, false);
		}

		if (!changedElements.includes($target)) {
			changedElements.push($target);
		}
	} else {
		const index = changedElements.indexOf($target);
		if (index > -1) {
			changedElements.splice(index, 1);
			markChangedField($target, false);
		}

		if (changedElements.length === 0) {
			removeBanner('#unsaved-changes-peachpay_message');
		}
	}
}

/**
 * Initializes the unsaved-changes banner by:
 *    1) selecting only the PeachPay settings fields,
 *    2) adding the original value attribute for later comparison, and
 *    3) listening for input/changes and calling the helper.
 */
function initUnsavedChangesBanner() {
	const $settingsContainer = document.querySelector('#peachpay_settings_container, #peachpay-gateway-settings');

	// selector for targetting only the settings fields
	let selector;
	if ($settingsContainer?.id === 'peachpay-gateway-settings' || $settingsContainer?.classList?.contains('advanced-gateway-settings')) {
		selector = 'input[name*="peachpay"], select[name*="peachpay"], textarea[name*="peachpay"]';
	} else if ($settingsContainer?.id === 'peachpay_settings_container') {
		const optionGroupName: string = $qs<HTMLInputElement>('input[name="option_page"]')?.value ?? '';
		selector = `input[name*="${optionGroupName}"], select[name*="${optionGroupName}"], textarea[name*="${optionGroupName}"]`;
	} else {
		return;
	}

	$settingsContainer?.querySelectorAll(selector).forEach(($element: HTMLElement) => {
		/* elements whose values change by drag-and-drop */
		if (/(billing|shipping|additional)_order/.test($element.getAttribute('name') ?? '')) {
			const $draggableElement = $element.closest('[draggable]')!;

			// set original value for later comparison; make sure the set value is n where this element is the nth element of this type
			$draggableElement?.setAttribute('data_originalValue', String(getValue($draggableElement as HTMLElement)));

			// event listener for reordering
			$draggableElement?.addEventListener('dragend', (event: Event) => {
				const $draggedElement = event.currentTarget as HTMLElement;
				let orderChanged = false;
				Array.from($draggedElement.parentElement!.children ?? []).forEach(($el, index) => {
					if ($el.getAttribute('data_originalValue') !== String(index)) {
						orderChanged = true;
					}
				});
				handleUnsavedChangesBanner($draggedElement.parentElement!, orderChanged);
			});
		} /* select2 element */ else if ($element.classList.contains('pp-select2')) {
			// select2 library does its rendering after the load
			window.addEventListener('load', () => {
				// set original value for later comparison
				$element.setAttribute('data_originalValue', String(getValue($element)));

				// mutation observer for changes to the selected options
				const observer = new MutationObserver((mutations: MutationRecord[]) => {
					for (const mutation of mutations) {
						if (mutation.type === 'childList') {
							const $target = (mutation.target as HTMLElement).closest('.select2')?.previousElementSibling as HTMLElement;
							handleUnsavedChangesBanner($target, elementHasChanged($target));
						}
					}
				});
				const $selectedItemsList = ($element.nextElementSibling as HTMLElement)?.querySelector('.select2-selection__rendered');
				if ($selectedItemsList) {
					observer.observe($selectedItemsList, {childList: true});
				}
			});
		} /* input, select, textarea */ else {
			// set original value for later comparison
			$element.setAttribute('data_originalValue', String(getValue($element)));

			// for the case of navigating back to the page with unsaved changes
			window.addEventListener('load', () => {
				handleUnsavedChangesBanner($element, elementHasChanged($element));
				markChangedField(($element.getAttribute('type') === 'radio' && $element.closest('section')) ? $element.closest('section')! : $element, elementHasChanged($element));
			});

			// event listener for input
			$element.addEventListener('input', (event: Event) => {
				event.stopPropagation();
				const $target = event.target as HTMLElement;
				handleUnsavedChangesBanner($target, elementHasChanged($target));
			});

			// mutation observer for changes to the value attribute
			const observer = new MutationObserver((mutations: MutationRecord[]) => {
				for (const mutation of mutations) {
					if (mutation.attributeName === 'value') {
						const $target = mutation.target as HTMLElement;
						handleUnsavedChangesBanner($target, elementHasChanged($target));
					}
				}
			});
			observer.observe($element, {attributes: true, attributeOldValue: true});
		}
	});
}

/**
 * Adds a listener for the form submission event to display the saving-changes banner.
 */
function initSavingChangesBanner() {
	const $settingsForm = document.querySelector('form[action="options.php"], form#mainform, .advanced-gateway-settings form[method="post"]')!;
	$settingsForm?.addEventListener('submit', () => {
		formSubmitted = true;
		removeBanner('#unsaved-changes-peachpay_message');
		removeBanner('#setting-error-peachpay_message.notice-success');
		addBanner('info', 'saving-changes', false, false);
	});
}

/**
 * Handles PeachPay banners that were already rendered on page load.
 */
function initExistingNotices() {
	document.querySelectorAll('.notice[id*="peachpay_message"]').forEach(($existingNotice: HTMLElement) => {
		$qs('.peachpay-notices-container')?.appendChild($existingNotice);
		$existingNotice.classList.add('addition');

		if ($existingNotice.id.includes('auto_dismiss')) {
			setTimeout(() => {
				$existingNotice.remove();
			}, 5_000);
		}
	});

	document.querySelectorAll('button.pp-notice-dismiss').forEach(($button: HTMLElement) => {
		$button.addEventListener('click', () => {
			const $notice = $button.closest('.notice');
			const id = $notice?.id;
			if (id) {
				removeBanner(`#${id}`);
			}
		});
	});
}

/**
 * Initializes the on-leave popup; after the popup appears, fields with unsaved changes will be highlighted.
 */
function initOnLeaveWarning() {
	const warning = () => {
		if (changedElements.length > 0 && !formSubmitted) {
			changedElements.forEach(($element: HTMLElement) => {
				markChangedField($element, true);
			});
			return 'You have unsaved changes! Are you sure you want to leave?';
		}

		return null;
	};

	window.onbeforeunload = warning;
}

/**
 * Adds the fixed position container for banners to the document and initializes the banner events.
 */
function initBanners() {
	if (!($qs('#peachpay_settings_container') ?? $qs('#peachpay-gateway-settings'))) {
		return;
	}

	initExistingNotices();
	initSavingChangesBanner();
	initOnLeaveWarning();
	initUnsavedChangesBanner();
}

export {initBanners};
